import { z } from 'zod'
import type { ChannelItem } from '@/types/api'

/**
 * Per-item settings form (pin version, duration override, validity window).
 * Server rules (ChannelController): duration_override_ms 1 000–86 400 000 and only on
 * slideshow items; valid_until after valid_from; pinned version must belong to the slideshow.
 */

export const LATEST = 'latest'
export const MIN_DURATION_S = 1
export const MAX_DURATION_S = 86_400

const optionalDateTime = z
  .string()
  .trim()
  .refine((v) => v === '' || !Number.isNaN(new Date(v).getTime()), 'Enter a valid date and time')

export const itemFormSchema = z
  .object({
    version: z.string().min(1),
    /** Seconds as typed; empty = one presentation cycle. */
    duration: z
      .string()
      .trim()
      .refine((v) => v === '' || /^\d+(\.\d+)?$/.test(v), 'Enter a number of seconds')
      .refine((v) => v === '' || (Number(v) >= MIN_DURATION_S && Number(v) <= MAX_DURATION_S), `Between ${MIN_DURATION_S} second and 24 hours`),
    valid_from: optionalDateTime,
    valid_until: optionalDateTime,
  })
  .superRefine((v, ctx) => {
    if (v.valid_from && v.valid_until && new Date(v.valid_until).getTime() <= new Date(v.valid_from).getTime()) {
      ctx.addIssue({ code: 'custom', path: ['valid_until'], message: 'Must be after the start' })
    }
  })

export type ItemFormValues = z.infer<typeof itemFormSchema>

/** ISO instant → `YYYY-MM-DDTHH:mm` in local time (for `<input type="datetime-local">`). */
export function toLocalInput(iso: string | null | undefined): string {
  if (!iso) return ''
  const d = new Date(iso)
  if (Number.isNaN(d.getTime())) return ''
  const pad = (n: number) => String(n).padStart(2, '0')
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`
}

/** Local `datetime-local` value → ISO instant (UTC), or null when empty. */
export function fromLocalInput(value: string): string | null {
  if (!value.trim()) return null
  const d = new Date(value)
  return Number.isNaN(d.getTime()) ? null : d.toISOString()
}

export function itemFormDefaults(item: Pick<ChannelItem, 'slideshow_version' | 'duration_override_ms' | 'valid_from' | 'valid_until'>): ItemFormValues {
  return {
    version: item.slideshow_version?.id ?? LATEST,
    duration: item.duration_override_ms ? String(item.duration_override_ms / 1000) : '',
    valid_from: toLocalInput(item.valid_from),
    valid_until: toLocalInput(item.valid_until),
  }
}

export interface ItemPatch {
  slideshow_version_id?: string | null
  duration_override_ms?: number | null
  valid_from: string | null
  valid_until: string | null
}

export function itemFormToPayload(values: ItemFormValues, itemType: ChannelItem['item_type']): ItemPatch {
  const base = { valid_from: fromLocalInput(values.valid_from), valid_until: fromLocalInput(values.valid_until) }
  if (itemType !== 'slideshow') return base
  return {
    ...base,
    slideshow_version_id: values.version === LATEST ? null : values.version,
    duration_override_ms: values.duration === '' ? null : Math.round(Number(values.duration) * 1000),
  }
}

/** Item condition (SIGNETIX_LOGIC.md §5) as edited in the settings dialog. */
export interface ItemCondition {
  rule_id: string | null
  unknown_as: boolean
}

export function itemConditionDefaults(item: Pick<ChannelItem, 'condition'>): ItemCondition {
  return { rule_id: item.condition?.rule_id ?? null, unknown_as: item.condition?.unknown_as ?? false }
}

/** `condition_rule_id` / `condition_unknown_as` for the PATCH — only when the condition changed. */
export function conditionPatch(initial: ItemCondition, next: ItemCondition): { condition_rule_id?: string | null; condition_unknown_as?: boolean } {
  const unknownAs = next.rule_id ? next.unknown_as : false
  if (initial.rule_id === next.rule_id && (initial.unknown_as === unknownAs || !next.rule_id)) return {}
  return { condition_rule_id: next.rule_id, condition_unknown_as: unknownAs }
}
